import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';

// MUI Components
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Grid2 as Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Modal,
  Select,
  Snackbar,
  Typography
} from '@mui/material';

// MUI Icons
import {
  LocalPostOffice as LocalPostOfficeIcon,
  LocalShipping as LocalShippingIcon,
  LocationCity as LocationCityIcon,
  LocationOn as LocationOnIcon,
  Map as MapIcon,
  PrivacyTipOutlined as PrivacyTipOutlinedIcon,
  Public as PublicIcon,
} from '@mui/icons-material';

// Contexts
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';

// Utilities & Helpers
import {
  fetchWrapper,
  fetchWrapperNoAuth,
  fetchWrapperPdf,
  fetchWrapperShipments,
  sendErrorMessage,
  SERVER
} from '../../utils/helpers';

// Components
import WalmartErrorHandler from '../WalmartErrorHandler';

// Modals
import ErrorModal from './ErrorModal';

// Tooltips & Loaders
import HtmlTooltip from '../Tooltip/HtmlTooltip';

// Inputs
import CustomDate from '../Inputs/CustomDate';
import CustomInput from '../Inputs/CustomInput';
import ProductPDFGenerator from '../ProductPDFGenerator';

function ShippmentsModal(props) {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { encodedAuth, user, password, userData, setUserData } = useAuth();

  const [items, setItems] = useState(props.items);
  const [openErrorModal, setOpenErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Address selection state
  const [selectedAddressIndex, setSelectedAddressIndex] = useState(userData?.addresses && userData.addresses.length > 0 ? 0 : -1);

  const [addressLine, setAddressLine] = useState(userData?.addresses?.[0]?.addressLine || "");
  const [city, setCity] = useState(userData?.addresses?.[0]?.city || "");
  const [stateCode, setStateCode] = useState(userData?.addresses?.[0]?.stateCode || "");
  const [countryCode, setCountryCode] = useState(userData?.addresses?.[0]?.countryCode || "US");
  const [postalCode, setPostalCode] = useState(userData?.addresses?.[0]?.postalCode || "");

  const [expectedDeliveryDate, setExpectedDeliveryDate] = useState(dayjs().add(14, 'days'));
  const [addOnServices, setAddOnServices] = useState([]);
  const [showServices, setShowServices] = useState(false);
  const availableAddOns = [ //"LABEL" / "BAG" / "TAPE" / "WRAP"
    { id: 1, name: 'Label', value: 'LABEL', disabled: false },
    // { id: 2, name: 'Bag', value: 'BAG', disabled: true },
    // { id: 3, name: 'Tape', value: 'TAPE', disabled: true },
    // { id: 4, name: 'Wrap', value: 'WRAP', disabled: true }
  ];

  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  // Handle address selection
  const handleAddressSelection = (index) => {
    setSelectedAddressIndex(index);

    if (index === -1) {
      // "Enter new address" option selected
      setAddressLine("");
      setCity("");
      setStateCode("");
      setCountryCode("US");
      setPostalCode("");
    } else {
      // Existing address selected
      const selectedAddress = userData?.addresses?.[index];
      if (selectedAddress) {
        setAddressLine(selectedAddress.addressLine || "");
        setCity(selectedAddress.city || "");
        setStateCode(selectedAddress.stateCode || "");
        setCountryCode(selectedAddress.countryCode || "US");
        setPostalCode(selectedAddress.postalCode || "");
      }
    }
  };

  // Format address for display in dropdown
  const formatAddressForDisplay = (address, index) => {
    const { addressLine, city, stateCode, postalCode } = address;
    return `${addressLine}, ${city}, ${stateCode} ${postalCode}`;
  };

  const handleClose = () => {
    props.setOpen(false);
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }

    setOpenSnackbar(false);
  };

  const alertError = (msg) => {
    setErrorMessage(msg);
    setOpenErrorModal(true);
  };

  const checkAllData = () => {
    if (items.length === 0) {
      alertError("No items selected. Please select items from the table.");
      return false;
    }

    const seen = new Map(); // Stores productId -> SKU
    const duplicates = new Map(); // Stores duplicate productId -> Array of SKUs
    for (let item of items) {
      if (item.publishedStatus !== "PUBLISHED") {
        alertError("Items with a status other than 'PUBLISHED' cannot be shipped.");
        return false;
      }
      if (item.qty < 1) {
        alertError(`Item with SKU ${item.sku} has a quantity of ${item.qty}. This should be a positive number.`);
        return;
      }
      const productId = item.gtin ? padCodeToGTIN(item.gtin) : padCodeToGTIN(item.upc);
      if (seen.has(productId)) {
        // If duplicate, store both original and new SKU
        if (!duplicates.has(productId)) {
          duplicates.set(productId, [seen.get(productId)]);
        }
        duplicates.get(productId).push(item.sku);
      } else {
        seen.set(productId, item.sku);
      }
    }
    if (duplicates.size > 0) {
      alertError(
        <div>
          <p>Duplicate GTIN{Array.from(duplicates.entries()).length > 1 && "'s"} found: (SKU's)</p>
          <ul>
            {Array.from(duplicates.entries()).map(([productId, skus]) => (
              <li key={productId}>
                <strong>{productId}</strong>: {skus.join(", ")}
              </li>
            ))}
          </ul>
        </div>
      )
      return false;
    }

    if (!addressLine) {
      alertError("Please enter a valid address.");
      return false;
    }
    if (!city) {
      alertError("Please enter a valid city name.");
      return false;
    }
    if (!stateCode || !/^[A-Z]{2}$/.test(stateCode)) {
      alertError("Please enter a valid state code. (exactly 2 uppercase letters, e.g., 'FL').");
      return false;
    }
    if (!countryCode || !/^[A-Z]{2}$/.test(countryCode)) {
      alertError("Please enter a valid country code (exactly 2 uppercase letters, e.g., 'US').");
      return false;
    }
    if (!postalCode) {
      alertError("Please enter a valid postal code.");
      return false;
    }

    if (dayjs(expectedDeliveryDate).isBefore(dayjs())) {
      alertError("Expected delivery date can't be in the past.");
      return false;
    }

    console.log("Validation successful!");
    return true;
  };

  const handleClick = async () => {
    if (!checkAllData()) {
      return;
    }
    setIsAnimating(true);

    try {
      // Execute the async function during the animation
      await handleSave(items);
    } catch (error) {
      console.error("Error during save:", error);
    }

    // Wait for the animation to complete before setting open to false
    setTimeout(() => {
      setIsAnimating(false);
      props.setOpen(false); // Call the provided prop function
    }, 2000); // Match the animation duration (500ms here)
  };

  const handleSave = async (items) => {
    const formattedDate = dayjs(expectedDeliveryDate).toISOString();
    const returnAddress = {
      "addressLine1": addressLine,
      "city": city,
      "stateCode": stateCode,
      "countryCode": countryCode,
      "postalCode": postalCode.toString()
    }
    const orderItems = items.map((item) => {
      const itemQty = parseInt(item.qty) || 1;
      const innerPack = parseInt(item.innerPack) || 1;
      const vendorPack = parseInt(itemQty / innerPack);
      const gtin = item.gtin ? padCodeToGTIN(item.gtin) : padCodeToGTIN(item.upc);
      return {
        "productId": gtin.trim(),
        "productType": "GTIN",
        "sku": item.sku,
        "itemDesc": (item.product_name || item.productName).slice(0, 450),
        "itemQty": parseInt(itemQty),
        "vendorPackQty": parseInt(vendorPack),
        "innerPackQty": parseInt(innerPack),
        "expectedDeliveryDate": formattedDate,
        "addOnServices": addOnServices.map(addOn => { return { serviceType: addOn } }) //  { "serviceType": "LABEL" / "BAG" / "TAPE" / "WRAP"}
      }
    })

    const body = {
      returnAddress: returnAddress,
      orderItems: orderItems
    }

    const response = await createInboundOrder(body);
    if (!response) {
      return;
    }
    const { shipment, inbound_order_id } = response;

    const successfulShipments = shipment.shipmentItems.map(item => {
      const insertedItem = items.find(i => i.sku === item.vendorSku);

      return {
        sku: item.vendorSku,
        qty: item.itemQty,
        image: insertedItem.image,
        itemId: insertedItem.itemId,
        gtin: insertedItem.gtin,
        productName: insertedItem.productName
      }
    });

    handleUpdateMetadata(inbound_order_id, shipment.shipmentId);

    // downloadInboundShipmentLabel(shipment.shipmentId);

    props.clearStoredData();

    setOpenSnackbar(true);
    handleClose();

    navigate('/shipments', {
      state: {
        inboundOrderId: inbound_order_id,
        showSuccessPopup: true,
        successfulShipments: successfulShipments
      }
    });
  };

  const createInboundOrder = async (body) => {
    const url = `${SERVER}/create_inbound_order`;
    try {
      const response = await fetchWrapperShipments(url, user, password, encodedAuth, { body: body });
      if (response.errors.length > 0) {
        handleErrors(response.errors, body);
        return;
      }

      return {
        shipment: response.shipment,
        inbound_order_id: response.inbound_order_id
      };
    } catch (error) {
      const errors = error?.errors || error?.error || [{ field: "An unknown error occurred" }];
      handleErrors(errors, body);
      return;
    }
  };

  const openShipmentsModal = () => {
    setErrorMessage("");
    setOpenErrorModal(false);
    props.setOpen(true);
  };

  const getItemID = (sku) => {
    const item = items.find(item => item.sku === sku);
    if (item) {
      return item.itemId;
    }
  };

  const handleErrors = (errors, body) => {
    if (errors.find(error => error.code === "UNAUTHORIZED.GMP_GATEWAY_API")) {
      alertError(
        <>
          <Typography sx={{ color: theme.text.primary, fontFamily: theme.fontFamily }}>WFS Program Eligibility is not enabled</Typography>
          <Typography sx={{ color: theme.text.primary, fontFamily: theme.fontFamily, textDecoration: 'underline', fontSize: 12, cursor: 'default', }} onClick={() => window.open('https://seller.walmart.com/wfs/wfs-onboarding')}>Click here to check on seller center</Typography>
        </>
      );
      return;
    }
    if (errors.find(error => error.code === "FORBIDDEN.GMP_GATEWAY_API")) {
      alertError(
        <>
          <Typography sx={{ color: theme.text.primary, fontFamily: theme.fontFamily }}>WFS Forbidden. Access denied</Typography>
          <Typography sx={{ color: theme.text.primary, fontFamily: theme.fontFamily, textDecoration: 'underline', fontSize: 12, cursor: 'default', }} onClick={() => window.open('https://seller.walmart.com/wfs/wfs-onboarding')}>Click here to check on seller center</Typography>
        </>
      );
      return;
    } else {
      sendErrorMessage(user, password, "WFS", '', '', "ShippmetsModal.js", -1, "createInboundOrder", "ERROR", errors, body);
      alertError(
        <WalmartErrorHandler
          errors={errors}
          body={body}
          openShipmentsModal={() => openShipmentsModal()}
          handleConvertToWFSUsingSC={handleConvertToWFSUsingSC}
          getItemID={getItemID}
          createInboundOrder={createInboundOrder}
          close={() => setOpenErrorModal(false)}
        />
      );
      return;
    }
  };

  const handleConvertToWFSUsingSC = async (sku) => {
    let wpid = null;
    try {
      const item = items.find(item => item.sku === sku);
      if (item && item.wpid) {
        wpid = item.wpid;
      } else if (item && item.anItem && item.anItem.wpid) {
        wpid = item.anItem.wpid
      } else {
        const body = { sku: sku };
        const url = `${SERVER}/get_an_item`;
        const itemData = await fetchWrapper(url, user, password, encodedAuth, { method: 'POST', body });
        const tempItem = itemData.ItemResponse[0];
        wpid = tempItem.wpid;
      }
      if (wpid) {
        window.open(`https://seller.walmart.com/catalog/item-maintenance/convert?type=wfs-convert&idType=productId&id=${wpid}&condition=New&conditionTypeCode=1&returnPATH=%2Fcatalog%2Flist-items`);
      }
    } catch (error) {
      console.log(error);
    }
  }

  const downloadInboundShipmentLabel = async (shipmentId) => {
    const url = `${SERVER}/create_inbound_shipment_label`;
    const body = {
      shipmentId: shipmentId
    }
    try {
      const blob = await fetchWrapperPdf(url, user, password, encodedAuth, { method: 'POST', body: body });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = 'example.pdf'; // Specify the download file name
      link.click();

    } catch (error) {
      console.error(`Create IO request failed:`, error.message);
    }
  };

  const handleUpdateMetadata = async (inboundOrderId, shipmentId) => {
    const tempItems = items.map((item) => {
      return {
        email: user,
        batchName: item.batch_name || "",
        productName: item.product_name || item.productName,
        productType: item.product_type,
        image: item.image || "",
        sku: item.sku,
        gtin: item.gtin,
        upc: item.upc,
        cost: parseFloat(item.cost) || 0,
        qty: parseInt(item.qty) || 1,
        innerPack: parseInt(item.inner_pack) || 1,
        source: item.source || "",
        inboundOrderId: inboundOrderId,
        shipmentId: shipmentId,
        note: item.note || "",
        expirationDate: item.expiration_date || "",
        purchaseDate: item.purchase_date || "",
        schema: item.schema || {},
        MPItem: item.mp_item || {},
        length: item["length"],
        width: item.width,
        height: item.height,
        weight: item.weight,
        additionalCosts: item.additional_costs
      }
    });

    const url = `${SERVER}/falcon_update_metadata`;
    const body = {
      items: tempItems
    }
    try {
      await fetchWrapperNoAuth(url, user, password, { method: 'POST', body: body });
    } catch (error) {
      console.error(error);
      return;
    }
  };

  const padCodeToGTIN = (code) => {
    if (code.length < 14) {
      return code.padStart(14, '0');
    }
    return code;
  };

  const handleDefaultAddress = async () => {
    if (!(addressLine && city && stateCode && countryCode && postalCode)) {
      alert('Fill all addres fields!');
      return;
    }

    const currentAddress = {
      addressLine: addressLine,
      city: city,
      stateCode: stateCode,
      countryCode: countryCode,
      postalCode: postalCode
    }

    const isSameAddress = (addr) =>
      addr.addressLine === addressLine &&
      addr.city === city &&
      addr.stateCode === stateCode &&
      addr.countryCode === countryCode &&
      addr.postalCode === postalCode;

    let tempAdresses = userData.addresses;

    const existingIndex = tempAdresses.findIndex(addr => isSameAddress(addr));

    if (existingIndex !== -1) {
      // Remove the existing address
      tempAdresses.splice(existingIndex, 1);
    }

    tempAdresses.unshift(currentAddress);

    const body = { email: user, addresses: tempAdresses };

    try {
      await fetchWrapperNoAuth(`${SERVER}/falcon_update_addresses`, user, password, { method: 'POST', body: body });
    } catch (e) {
      console.error(e);
    }

    setUserData({ ...userData, addresses: tempAdresses });
  };

  const handleCheckboxChange = (event, addOn) => {
    const { checked } = event.target;

    if (checked) {
      // Add the value to addOnServices array
      setAddOnServices([...addOnServices, addOn.value]);
    } else {
      // Remove the value from addOnServices array
      setAddOnServices(addOnServices.filter(service => service !== addOn.value));
    }
  };

  useEffect(() => {
    setItems(props.items);
  }, [props.items]);

  const styles = {
    dateInputLabel: {
      fontFamily: theme.fontFamily,
      color: theme.text.primary,
      '&.Mui-focused': {
        color: theme.color.primary, // Change label color on focus
      },
    },
    dateOutlinedInput: {
      fontFamily: theme.fontFamily,
      color: theme.text.primary,
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.text.primary,
      },
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.text.primary,
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.color.primary,
      },
    },
    modal: {
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width: '46vw',
      bgcolor: theme.background.primary,
      borderRadius: 2,
      border: `4px solid ${theme.color.primary}`,
      boxShadow: 24,
      p: 4,
      outline: 'none',
    },
    card: {
      flex: 1,
      padding: 2,
      border: `1px solid ${theme.border.dark}`,
      borderRadius: 2,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      textTransform: 'none',
      backgroundColor: theme.background.secondary,
      color: theme.text.primary,
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: theme.background.tertiary,
        boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
      },
      '&.Mui-disabled': {
        opacity: 0.5,
        backgroundColor: theme.accent.disabled,
      }
    },
    button: {
      textTransform: 'none',
      fontFamily: theme.fontFamily,
      color: theme.text.negative,
      borderRadius: '20px',
      border: `1px solid ${theme.border.light}`,
      backgroundColor: theme.accent.error,
    },
    selectInput: {
      fontFamily: theme.fontFamily,
      color: theme.text.primary,
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.text.primary,
      },
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.text.primary,
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.color.primary,
      },
      '& .MuiSelect-select': {
        color: theme.text.primary,
      }
    }
  };

  return (
    <>
      <Modal open={props.open} onClose={handleClose}>
        <Box sx={styles.modal}>
          <Typography variant="h5" component="h2" gutterBottom style={{ fontFamily: theme.fontFamily, textAlign: 'center', color: theme.text.primary }}>
            Create Inbound Shippment - {items.length} items
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%", px: 2, mb: 2 }}>
            <Typography component="body2" gutterBottom style={{ fontFamily: theme.fontFamily, textAlign: 'center', color: theme.text.tertiary }}>
              Total units - {items.reduce((acc, item) => acc + (item.qty || 0), 0)}
            </Typography>
            <ProductPDFGenerator products={items} />
          </Box>

          {/* Form */}
          <Grid container spacing={5} sx={{ justifyContent: 'center', color: theme.text.primary }}>
            {/* Address Information */}
            <Grid size={6}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Typography variant="h6" sx={{ fontFamily: theme.fontFamily, mt: 1 }}>
                  Address Information
                </Typography>

                {/* Address Selection Dropdown */}
                {userData?.addresses && userData.addresses.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontFamily: theme.fontFamily, mb: 1, color: theme.text.secondary }}>
                      Select Address
                    </Typography>
                    <Select
                      value={selectedAddressIndex}
                      onChange={(e) => handleAddressSelection(e.target.value)}
                      size="small"
                      fullWidth
                      sx={styles.selectInput}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            bgcolor: theme.background.primary,
                            '& .MuiMenuItem-root': {
                              color: theme.text.primary,
                              fontFamily: theme.fontFamily,
                              '&:hover': {
                                backgroundColor: theme.background.secondary,
                              },
                              '&.Mui-selected': {
                                backgroundColor: theme.color.secondary,
                                color: theme.text.negative,
                                '&:hover': {
                                  backgroundColor: theme.color.primary,
                                }
                              }
                            }
                          }
                        }
                      }}
                    >
                      <MenuItem value={-1}>
                        <Typography variant="body2" sx={{ fontFamily: theme.fontFamily, fontStyle: 'italic' }}>
                          Enter new address
                        </Typography>
                      </MenuItem>
                      {userData.addresses.map((address, index) => (
                        <MenuItem key={index} value={index}>
                          <Typography variant="body2" sx={{ fontFamily: theme.fontFamily }}>
                            {formatAddressForDisplay(address, index)}
                          </Typography>
                        </MenuItem>
                      ))}
                    </Select>
                  </Box>
                )}

                <CustomInput
                  fullWidth
                  size="small"
                  label={"Address Line"}
                  value={addressLine}
                  onChange={(e) => setAddressLine(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LocationOnIcon sx={{ color: (!addressLine || addressLine === "") ? theme.accent.error : theme.color.primary }} />
                      </InputAdornment>
                    )
                  }}
                  isError={!addressLine || addressLine === ""}
                />
                <CustomInput
                  fullWidth
                  size="small"
                  label={"City"}
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LocationCityIcon sx={{ color: (!city || city === "") ? theme.accent.error : theme.color.primary }} />
                      </InputAdornment>
                    )
                  }}
                  isError={!city || city === ""}
                />
                <CustomInput
                  fullWidth
                  size="small"
                  label={"State Code"}
                  value={stateCode}
                  onChange={(e) => setStateCode(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <MapIcon sx={{ color: (!stateCode || stateCode === "") ? theme.accent.error : theme.color.primary }} />
                      </InputAdornment>
                    )
                  }}
                  isError={!stateCode || stateCode === ""}
                />
                <CustomInput
                  fullWidth
                  size="small"
                  label={"Country Code"}
                  value={countryCode}
                  onChange={(e) => setCountryCode(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PublicIcon sx={{ color: (!countryCode || countryCode === "") ? theme.accent.error : theme.color.primary }} />
                      </InputAdornment>
                    )
                  }}
                  isError={!countryCode || countryCode === ""}
                />
                <CustomInput
                  fullWidth
                  size="small"
                  label={"Postal Code"}
                  value={postalCode}
                  onChange={(e) => setPostalCode(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LocalPostOfficeIcon sx={{ color: (!postalCode || postalCode === "") ? theme.accent.error : theme.color.primary }} />
                      </InputAdornment>
                    )
                  }}
                  isError={!postalCode || postalCode === ""}
                />
              </Box>
            </Grid>

            {/* Basic Information */}
            <Grid size={6}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Typography variant="h6" sx={{ fontFamily: theme.fontFamily, mt: 1 }}>
                  Expected Delivery Date
                </Typography>

                <CustomDate
                  value={expectedDeliveryDate}
                  onChange={(newValue) => {
                    // Ensure newValue is converted to dayjs if it isn't already
                    const dayjsValue = dayjs(newValue);
                    setExpectedDeliveryDate(dayjsValue);
                  }}
                  label="Expected Delivery Date"
                />

                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-start', }}>
                    <HtmlTooltip
                      customtheme={theme}
                      title={
                        <Typography sx={{ color: theme.text.primary, fontFamily: theme.fontFamily, fontSize: 12 }}>
                          click to select add on services (EXTRA FEES)
                        </Typography>
                      }
                      arrow
                      placement="top"
                      slotProps={{
                        popper: {
                          modifiers: [
                            {
                              name: 'offset',
                              options: {
                                offset: [0, -15],
                              },
                            },
                          ],
                        },
                      }}
                    >
                      <IconButton
                        onClick={() => setShowServices(!showServices)}
                        sx={{ color: theme.color.primary, }}
                      >
                        <PrivacyTipOutlinedIcon />
                      </IconButton>
                    </HtmlTooltip>
                    {showServices && (
                      <Typography variant="h6" sx={{ fontFamily: theme.fontFamily }}>
                        Add-On Services
                      </Typography>
                    )}
                  </Box>

                  {showServices && (
                    <FormGroup>
                      {availableAddOns.map((addOn) => (
                        <FormControlLabel
                          key={addOn.id}
                          control={
                            <Checkbox
                              onChange={(e) => handleCheckboxChange(e, addOn)}
                              checked={addOnServices.includes(addOn.value)}
                              sx={{
                                '& .MuiSvgIcon-root': {
                                  fill: addOn.disabled ? theme.accent.disabled : theme.text.primary, // Icon color inside the checkbox
                                },
                                ml: '15px',
                                p: '5px'
                              }}
                              disabled={addOn.disabled}
                            />
                          }
                          label={<Typography variant="subtitle1" sx={{ fontFamily: theme.fontFamily, color: addOn.disabled ? theme.accent.disabled : theme.text.primary }}>{addOn.name}</Typography>}
                        />
                      ))}
                    </FormGroup>
                  )}

                </Box>
              </Box>
            </Grid>

          </Grid>

          {/* Cancel and Save buttons */}
          <Box sx={{ display: 'flex', justifyContent: 'space-around', mt: 3 }}>
            <Button
              variant="contained"
              onClick={handleDefaultAddress}
              disabled={!(addressLine && city && stateCode && countryCode && postalCode)}
              sx={{
                textTransform: 'none',
                fontFamily: theme.fontFamily,
                color: theme.text.negative,
                borderRadius: '20px',
                backgroundColor: theme.accent.success,
                border: `1px solid ${theme.border.light}`
              }}
            >
              Set as default address
            </Button>

            <Button
              variant="contained"
              onClick={handleClick}
              disabled={isAnimating}
              sx={{
                textTransform: 'none',
                fontFamily: theme.fontFamily,
                color: theme.text.negative,
                backgroundColor: theme.color.primary,
                borderRadius: '20px',
                border: `1px solid ${theme.border.light}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
                overflow: "hidden",
                position: "relative",
                width: 200,
                transition: "all 2s ease-in-out",
                "& .icon": {
                  position: "absolute",
                  left: isAnimating ? "calc(100% - 32px)" : "8px",
                  transition: "left 2s linear",
                },
                "& .text": {
                  opacity: isAnimating ? 0 : 1,
                  transition: "opacity 2s ease-in-out",
                },
              }}
            >
              <LocalShippingIcon className="icon" />
              <span className="text" style={{ marginLeft: '25px' }}>Create<sub style={{ fontSize: '12' }}> (beta)</sub></span>
            </Button>
          </Box>
        </Box>
      </Modal >

      <ErrorModal open={openErrorModal} setOpen={setOpenErrorModal} message={errorMessage} />

      <Snackbar open={openSnackbar} autoHideDuration={3000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'top', horizontal: 'center' }} sx={{ zIndex: 99999999999 }}>
        <Alert
          onClose={handleCloseSnackbar}
          severity="success"
          variant="filled"
          sx={{ width: '100%' }}
        >
          Inbound shipment has been created! You are being redirected to the Shipments page to view your shipments!
        </Alert>
      </Snackbar>
    </>
  );
}

export default ShippmentsModal;